import { Card } from 'nhsuk-react-components';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import Back from 'components/Back/index.tsx';
import RoutingIDsForm from 'components/forms/RoutingIDs/RoutingIDs.tsx';
import IterationPageHeader from 'components/IterationPageHeader/index.tsx';
import useCampaign from 'hooks/useCampaign.ts';
import useIterationLock from 'hooks/useIterationLock.ts';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import ProgressIndicator from 'components/ProgressIndicator/index.tsx';
import { IIterationLevelParams } from 'utils/parameterUtils';
import useUpdateIteration from 'queries/mutate/updateIteration.ts';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { getIterationById } from 'utils/iterationUtils.ts';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import { ApiError } from 'schemas/ApiError.schema';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import useFetchTeamRoutingPlans from 'queries/fetch/fetchTeamRoutingPlans';
import featureFlags from 'services/featureFlags';
import { hasOnlyUnversionedRoutingPlans } from 'utils/routingPlansUtils';

export default function IterationRoutingPlanPage() {
  const { campaign } = useCampaign();
  const { teamId, campaignId, iterationId } = useParams() as IIterationLevelParams;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const updateIteration = useUpdateIteration();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });
  const { navigateToCampaign } = useCampaignNavigate();
  useProtectRoute();

  const { lockIterationIfRequired, error: lockError } = useIterationLock({ teamId, campaignId, iterationId });
  const initialRenderRef = useRef(true);
  const existingIterations = campaign.CampaignConfig?.Iterations || [];
  const existingIteration = existingIterations.find(
    (iteration) => iteration.ID === iterationId,
  );

  const existingCommsRouting = existingIterations.find((iteration) => iteration.ID === iterationId)
    ?.DefaultCommsRouting || '';
  const existingRoutingIDs = existingCommsRouting
    .split('|')
    .filter((id) => id !== '');
  const shouldShowUnversionedRoutingPlans = !featureFlags.notifyRoutingPlansEnabled
    || hasOnlyUnversionedRoutingPlans(existingRoutingIDs);

  const { data: teamRoutingPlans } = useFetchTeamRoutingPlans({ teamId, enabled: featureFlags.notifyRoutingPlansEnabled });

  const onSuccessfulSubmission = async (routingPlan: string) => {
    setApiError(null);

    const currentIteration = getIterationById(campaign, iterationId);
    const updatedIteration = {
      ...currentIteration,
      DefaultCommsRouting: routingPlan,
    };

    try {
      setIsSubmitting(true);
      await updateIteration.mutateAsync({
        campaignId,
        iteration: updatedIteration as IterationDetailsFormValues,
        iterationId,
        teamId,
      });

      await fetchSpecificCampaign.refetch();
      const redirectPage = searchParams.get('redirectPage');

      if (redirectPage) {
        navigateToCampaign(redirectPage);
        return;
      }

      navigateToCampaign(`iteration/${iterationId}/summary`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred saving the message plan';
      setApiError({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      lockIterationIfRequired();
    }
  }, [lockIterationIfRequired]);

  if (!existingIteration) {
    return <ProgressIndicator text="Loading iteration details..." />;
  }

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Iteration summary" backTo={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`} />
      </div>
      {existingIteration && (
        <IterationPageHeader
          iteration={existingIteration}
          title="Default message plans"
        />
      )}
      {shouldShowUnversionedRoutingPlans && (
        <Card>
          <>
            <h3>Add at least one message plan</h3>
            <p>
              Use the dropdown to find your message plan. Click on &apos;Add&apos; to select it. Do this for all message plans you want to add.
            </p>
          </>
        </Card>
      )}

      <RoutingIDsForm
        onSuccessfulSubmission={onSuccessfulSubmission}
        existingRoutingIDs={existingRoutingIDs}
        teamRoutingPlans={teamRoutingPlans || []}
        isSubmitting={isSubmitting}
      />
      <ApiErrorDetails apiError={apiError || lockError} />
    </div>
  );
}
